"use client"

import { ChatMarkdownBody } from "@/components/ui/markdown-message"
import { tokenizeMessageText } from "@/lib/conversation/message-text-format"

/**
 * Message text rendered the way the customer saw it, wherever a conversation is
 * read.
 *
 * `markdown` is the restricted subset we author on channels that render it — the
 * caller decides, because only it knows the channel and which side wrote this.
 * Everything else is literal, with clickable links and WhatsApp-style inline
 * marks (bold, italic, strike — Meta delivers them as raw markers).
 *
 * Links inherit the bubble's text colour rather than fixing their own, so they
 * stay readable on every tone the shared bubble paints.
 */
export function ConversationMessageText({
  text,
  markdown = false,
}: {
  text: string
  markdown?: boolean
}) {
  if (markdown) return <ChatMarkdownBody text={text} />

  return (
    <p className="whitespace-pre-wrap">
      {tokenizeMessageText(text).map((token, index) => {
        switch (token.kind) {
          case "link":
            return (
              <a
                key={index}
                href={token.href}
                target="_blank"
                rel="noopener noreferrer"
                className="break-all underline underline-offset-2 hover:opacity-80"
              >
                {token.href}
              </a>
            )
          case "bold":
            return <strong key={index}>{token.text}</strong>
          case "italic":
            return <em key={index}>{token.text}</em>
          case "strike":
            return <s key={index}>{token.text}</s>
          default:
            return token.text
        }
      })}
    </p>
  )
}
